import { useState } from 'react'

// Default tiers used if none provided via props
const DEFAULT_TIERS = [
  { kw: 1,  cost: 70000  },
  { kw: 2,  cost: 140000 },
  { kw: 3,  cost: 180000 },
  { kw: 5,  cost: 265000 },
  { kw: 8,  cost: 400000 },
  { kw: 10, cost: 460000 },
]

/**
 * Given a required system size (kW), snap UP to the nearest pricing tier.
 * For sizes above the largest tier, extrapolate using per-kW rate of the
 * largest tier.
 */
const getSystemCostFromTiers = (requiredKw, tiers) => {
  // Sort tiers ascending just in case
  const sorted = [...tiers].sort((a, b) => a.kw - b.kw)

  // Find the first tier that covers the required size
  const tier = sorted.find(t => t.kw >= requiredKw)
  if (tier) {
    return { recommendedKw: tier.kw, cost: tier.cost }
  }

  // requiredKw exceeds largest tier – extrapolate proportionally
  const largest = sorted[sorted.length - 1]
  const ratePerKw = largest.cost / largest.kw          // e.g. ₹46,000/kW
  const extraKw = requiredKw - largest.kw
  const extraCost = extraKw * ratePerKw * 0.90          // ~10% bulk discount
  return {
    recommendedKw: parseFloat(requiredKw.toFixed(1)),
    cost: Math.round(largest.cost + extraCost),
  }
}

export const SavingsCalculator = (props) => {
  const cfg = props.data || {}
  const cities = cfg.cities || []
  const defaultSunHours = cfg.sunHoursPerDay || 5
  const costPerKwh = cfg.costPerKwh || 9
  const pricingTiers = (cfg.pricingTiers && cfg.pricingTiers.length) ? cfg.pricingTiers : DEFAULT_TIERS

  const [tab, setTab] = useState('residential')
  const [bill, setBill] = useState('')
  const [city, setCity] = useState('')
  const [sanctionedLoad, setSanctionedLoad] = useState('')
  const [result, setResult] = useState(null)

  const calculate = () => {
    const monthlyBill = parseFloat(bill)
    if (!monthlyBill || monthlyBill <= 0) return

    const selectedCity = cities.find(c => c.name === city)
    const sunHours = selectedCity ? selectedCity.sunHours : defaultSunHours

    // Raw system size needed to cover the bill
    const rawSystemKw = monthlyBill / (costPerKwh * sunHours * 30)

    // Snap to nearest tier and get the actual cost
    const { recommendedKw, cost: systemCost } = getSystemCostFromTiers(rawSystemKw, pricingTiers)

    const annualSavings = monthlyBill * 12 * 0.80

    // PM Surya Ghar Yojana subsidy (residential only)
    // ≤1 kW → ₹30,000 | ≤2 kW → ₹60,000 | ≤3 kW → ₹78,000 | >3 kW → ₹78,000
    let subsidy = 0
    if (tab === 'residential') {
      if (recommendedKw <= 1)      subsidy = 30000
      else if (recommendedKw <= 2) subsidy = 60000
      else                         subsidy = 78000
    }

    const netCost = systemCost - subsidy
    const paybackYears = annualSavings > 0 ? netCost / annualSavings : 0
    const co2PerYear = recommendedKw * 1.5

    setResult({
      systemSizeKw: recommendedKw,
      annualSavings: Math.round(annualSavings),
      systemCost,
      subsidy,
      netCost,
      paybackYears: paybackYears.toFixed(1),
      co2PerYear: co2PerYear.toFixed(1),
    })
  }

  const fmt = (n) => '₹' + n.toLocaleString('en-IN')

  return (
    <div id='calculator' className='text-center'>
      <div className='container'>
        <div className='row'>
          <div className='col-md-10 col-md-offset-1 section-title'>
            <h2>Solar Savings Calculator</h2>
            <p>Find out how much you can save by switching to solar. Enter your monthly electricity bill to get started.</p>
          </div>
        </div>

        <div className='calc-wrapper'>
          {/* Tabs */}
          <div className='calc-tabs'>
            <button
              className={`calc-tab ${tab === 'residential' ? 'active' : ''}`}
              onClick={() => { setTab('residential'); setResult(null) }}
            >
              <i className='fa fa-home'></i> Residential / Rooftop
            </button>
            <button
              className={`calc-tab ${tab === 'commercial' ? 'active' : ''}`}
              onClick={() => { setTab('commercial'); setResult(null) }}
            >
              <i className='fa fa-building'></i> Commercial / Industrial
            </button>
          </div>

          <div className='calc-body'>
            <div className='row'>
              <div className='col-md-6 col-sm-6 col-xs-12'>
                <div className='form-group calc-form-group'>
                  <label>Monthly Electricity Bill (₹)</label>
                  <input
                    type='number'
                    className='form-control calc-input'
                    placeholder={tab === 'residential' ? 'e.g. 3000' : 'e.g. 50000'}
                    value={bill}
                    onChange={e => { setBill(e.target.value); setResult(null) }}
                    min='0'
                  />
                </div>
              </div>
              <div className='col-md-6 col-sm-6 col-xs-12'>
                <div className='form-group calc-form-group'>
                  <label>Your City</label>
                  <select className='form-control calc-input' value={city} onChange={e => { setCity(e.target.value); setResult(null) }}>
                    <option value=''>Select city</option>
                    {cities.map(c => <option key={c.name} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
              </div>
              {tab === 'commercial' && (
                <div className='col-md-6 col-sm-6 col-xs-12'>
                  <div className='form-group calc-form-group'>
                    <label>Sanctioned Load (kW) <span style={{ fontWeight: 400, fontSize: '12px' }}>optional</span></label>
                    <input
                      type='number'
                      className='form-control calc-input'
                      placeholder='e.g. 100'
                      value={sanctionedLoad}
                      onChange={e => setSanctionedLoad(e.target.value)}
                      min='0'
                    />
                  </div>
                </div>
              )}
            </div>

            <button className='btn btn-custom btn-lg calc-btn' onClick={calculate}>
              Calculate My Savings <i className='fa fa-arrow-right'></i>
            </button>

            {result && (
              <div className='calc-results'>
                <div className='row'>
                  <div className='col-md-3 col-sm-6 col-xs-6'>
                    <div className='result-card'>
                      <i className='fa fa-bolt result-icon'></i>
                      <div className='result-value'>{result.systemSizeKw} kW</div>
                      <div className='result-label'>Recommended System Size</div>
                    </div>
                  </div>
                  <div className='col-md-3 col-sm-6 col-xs-6'>
                    <div className='result-card'>
                      <i className='fa fa-rupee result-icon'></i>
                      <div className='result-value'>{fmt(result.systemCost)}</div>
                      <div className='result-label'>System Cost (Gross)</div>
                    </div>
                  </div>
                  <div className='col-md-3 col-sm-6 col-xs-6'>
                    <div className='result-card'>
                      <i className='fa fa-money result-icon'></i>
                      <div className='result-value'>{fmt(result.annualSavings)}</div>
                      <div className='result-label'>Estimated Annual Savings</div>
                    </div>
                  </div>
                  <div className='col-md-3 col-sm-6 col-xs-6'>
                    <div className='result-card'>
                      <i className='fa fa-clock-o result-icon'></i>
                      <div className='result-value'>{result.paybackYears} yrs</div>
                      <div className='result-label'>Payback Period</div>
                    </div>
                  </div>
                </div>

                <div className='row' style={{ marginTop: '12px' }}>
                  <div className='col-md-6 col-sm-6 col-xs-6'>
                    <div className='result-card'>
                      <i className='fa fa-leaf result-icon'></i>
                      <div className='result-value'>{result.co2PerYear} T</div>
                      <div className='result-label'>CO₂ Offset / Year</div>
                    </div>
                  </div>
                  {tab === 'residential' && result.subsidy > 0 && (
                    <div className='col-md-6 col-sm-6 col-xs-6'>
                      <div className='result-card highlight-card'>
                        <i className='fa fa-tags result-icon'></i>
                        <div className='result-value'>{fmt(result.netCost)}</div>
                        <div className='result-label'>Net Cost After Subsidy</div>
                      </div>
                    </div>
                  )}
                </div>

                {tab === 'residential' && result.subsidy > 0 && (
                  <div className='subsidy-note'>
                    <i className='fa fa-info-circle'></i>&nbsp;
                    You are eligible for a <strong>{fmt(result.subsidy)}</strong> government subsidy under <strong>PM Surya Ghar Yojana</strong>, bringing your net system cost to <strong>{fmt(result.netCost)}</strong>.
                  </div>
                )}
                <div style={{ marginTop: '24px' }}>
                  <a href='#contact' className='btn btn-custom btn-lg'>
                    Get a Detailed Quote &nbsp;<i className='fa fa-envelope'></i>
                  </a>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

